<template>
	<div>
		<div class="cs-head">
			<div>
				<h1 class="cs-title">Finance</h1>
				<p class="cs-sub">Finance sets the numbers sales quote from and decides the quotes that fall outside them. What the client needs, the scope and the timeline stay with sales. Four jobs, in the order they come up; each card opens the screen where the work is done.</p>
			</div>
		</div>

		<pe-verdict v-for="d in drafts" :key="d.id" tone="warn" style="margin-bottom:12px" :title="'Draft v' + d.draft.version_no + ' of ' + d.name + ' is not published'" text="Quotes keep using the published prices until you publish the draft on Floors and limits." />

		<div class="pe-fin-jobs">
			<div class="pe-fin-job" :class="{ done: loaded && !waiting.length }">
				<h5><span class="pe-fin-step">1</span>Decide quotes</h5>
				<div class="n">{{ loaded ? waiting.length : '…' }}<small>{{ waiting.length === 1 ? 'quote waits' : 'quotes wait' }} for you</small></div>
				<p>Sales sends a quote here when it has a discount, a price request or a custom line. You get an email with a link. Decide it, block it while you wait on an answer, or return it to sales, ideally within 2 business days.</p>
				<p v-if="overClock" class="badge-warn" style="align-self:flex-start">{{ overClock }} waiting longer than 2 business days</p>
				<div class="go"><router-link class="btn-primary" to="/pricing/admin/quotes">Open quotes</router-link></div>
			</div>

			<div class="pe-fin-job" :class="{ done: loaded && !floors.missing }">
				<h5><span class="pe-fin-step">2</span>Set floors and limits</h5>
				<div class="n">{{ loaded ? floors.missing : '…' }}<small>of {{ floors.total }} prices have no floor</small></div>
				<p>A floor is the lowest price a discount can reach without you. The max discount caps the percent. Until a price has them, every discount on it comes to you. Our cost stays internal and feeds the margin.</p>
				<div class="go"><router-link class="btn-primary" to="/pricing/admin/floors">Set floors and limits</router-link></div>
			</div>

			<div class="pe-fin-job" :class="{ done: loaded && !values.missing }">
				<h5><span class="pe-fin-step">3</span>Write bundle values</h5>
				<div class="n">{{ loaded ? values.missing : '…' }}<small>of {{ values.total }} bundle parts have no value</small></div>
				<p>A bundle is one price for several parts. Write what each part is worth inside it and one line on why that differs from its own price. It never changes a quote; it shows the margin per part.</p>
				<div class="go"><router-link class="btn-primary" to="/pricing/admin/bundles">Write bundle values</router-link></div>
			</div>

			<div class="pe-fin-job" :class="{ done: loaded && !precedents }">
				<h5><span class="pe-fin-step">4</span>Turn precedents into SKUs</h5>
				<div class="n">{{ loaded ? precedents : '…' }}<small>{{ precedents === 1 ? 'custom line is' : 'custom lines are' }} ready</small></div>
				<p>A custom line on a won quote is a precedent. Give it a code and it becomes a SKU with a draft price, so the next quote with it does not need you.</p>
				<div class="go"><router-link class="btn-primary" to="/pricing/admin/reports">Open precedents</router-link></div>
			</div>
		</div>

		<h6 class="bd-grp" style="margin-top:26px">Settings, changed rarely</h6>
		<div class="pe-fin-set" style="margin-top:8px">
			<router-link to="/pricing/admin/catalog"><b>Price list</b><span>Every SKU, what a bundle includes and the list price per population band. A price change is a new version with a note.</span></router-link>
			<router-link to="/pricing/admin/rules"><b>Rules</b><span>The HeyGov minimum, support tiers, approval limits and which price list each sale reads.<template v-if="openRules"> {{ openRules }} {{ openRules === 1 ? 'rule needs' : 'rules need' }} a decision from the company owner.</template></span></router-link>
			<router-link to="/pricing/admin/competitors"><b>Competitor prices</b><span>What other website vendors charge towns of each size. Sales see them next to the quote.</span></router-link>
			<router-link to="/pricing/admin/reports"><b>Reports</b><span>How long quotes wait for finance, where the discounts go, and the parallel run against the spreadsheets.</span></router-link>
		</div>
	</div>
</template>

<script>
import restApi from '../../../api/index.js'
import { PeVerdict } from '../../../components/pe'
import { errorText, ruleNeedsDecision } from '../pe-util'

export default {
	name: 'PricingFinanceHome',
	components: { PeVerdict },
	data() {
		return { loaded: false, quotes: [], skus: [], compositions: [], precedentRows: [], schedules: [], rules: [] }
	},
	computed: {
		waiting() { return this.quotes.filter(q => ['ready_for_finance', 'blocked'].includes(q.state)) },
		overClock() { return this.waiting.filter(q => q.time_in_state && q.time_in_state.business_days > 2).length },
		floors() {
			const rows = this.skus.filter(s => !['inactive', 'retired'].includes(s.status)).flatMap(s => s.prices || [])
			return { total: rows.length, missing: rows.filter(p => p.floor_cents === null).length }
		},
		values() {
			const parts = this.compositions.flatMap(c => (c.bands[0] ? c.bands[0].parts : []))
			return { total: parts.length, missing: parts.filter(p => p.value_cents === null).length }
		},
		precedents() { return this.precedentRows.filter(p => !p.promoted_sku_id).length },
		drafts() { return this.schedules.filter(s => s.draft) },
		openRules() { return this.rules.filter(ruleNeedsDecision).length },
	},
	created() {
		Promise.all([restApi.get('/pricing/quotes', { params: { perPage: 200 } }), restApi.get('/pricing/skus', { params: { quotable: 'all' } }), restApi.get('/pricing/compositions'),
			restApi.get('/pricing/precedents'), restApi.get('/pricing/schedules'), restApi.get('/pricing/rules')])
			.then(([q, s, c, p, sc, r]) => { this.quotes = q.data.data; this.skus = s.data; this.compositions = c.data; this.precedentRows = p.data; this.schedules = sc.data; this.rules = r.data; this.loaded = true })
			.catch(err => this.$root.$emit('pe-notice', { tone: 'warn', text: errorText(err, 'Could not load the finance jobs') }))
	},
}
</script>
